import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { functions, httpsCallable } from '../firebase';
import { Button, Card } from '../components';
import LottieSpinner from '../components/LottieSpinner';

/**
 * /claim/:listingId#token=…  — landing van de claim-link uit de HomeMe-mails.
 * Het token staat in het URL-fragment (blijft buiten server-logs) en wordt in
 * sessionStorage bewaard zodat het een login/registratie-rondje overleeft.
 * De Cloud Function `claimWebListing` verifieert het token, zet agentUid en
 * keurt het agent-account automatisch goed (token = bewijs van eigenaarschap).
 */
const PENDING_KEY = 'homeme_pending_claim';

export const storePendingClaim = (listingId: string, token: string) => {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ listingId, token })); } catch { /* private mode */ }
};
export const getPendingClaim = (): { listingId: string; token: string } | null => {
  try { return JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null'); } catch { return null; }
};
export const clearPendingClaim = () => { try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ } };

export const ClaimPage: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const { currentUser, loading, setGlobalNotification } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<'init' | 'claiming' | 'error'>('init');
  const [error, setError] = useState('');
  const claimedRef = useRef(false);

  // Token uit #fragment halen en direct opslaan (ook vóór login)
  useEffect(() => {
    const m = window.location.hash.match(/token=([A-Za-z0-9_-]+)/);
    if (m && listingId) storePendingClaim(listingId, m[1]);
  }, [listingId]);

  useEffect(() => {
    const pending = getPendingClaim();
    if (loading || !currentUser || !pending || pending.listingId !== listingId || claimedRef.current) return;
    claimedRef.current = true;
    setState('claiming');
    const claim = httpsCallable(functions, 'claimWebListing');
    claim({ listingId: pending.listingId, claimToken: pending.token })
      .then(() => {
        clearPendingClaim();
        setGlobalNotification({ message: 'Advertentie geclaimd! Je ziet nu je kandidaten en chats.', type: 'success' });
        navigate('/dashboard');
      })
      .catch((e: any) => {
        claimedRef.current = false;
        setError(e?.message || 'Claimen mislukt');
        setState('error');
      });
  }, [loading, currentUser, listingId, navigate, setGlobalNotification]);

  const pending = getPendingClaim();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md p-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Advertentie claimen</h1>
        {!pending && state !== 'claiming' ? (
          <>
            <p className="text-gray-600 mb-4">Deze claim-link is onvolledig of verlopen. Open de knop uit je HomeMe-mail opnieuw, of mail <a className="underline" href="mailto:info@homeme.app">info@homeme.app</a>.</p>
          </>
        ) : loading || state === 'claiming' ? (
          <>
            <LottieSpinner />
            <p className="text-gray-600 mt-2">{state === 'claiming' ? 'Je advertentie wordt gekoppeld…' : 'Even laden…'}</p>
          </>
        ) : !currentUser ? (
          <>
            <p className="text-gray-600 mb-6">Log in of maak gratis een account aan om je kandidaten te bekijken en te chatten. Je advertentie wordt daarna automatisch gekoppeld.</p>
            <div className="flex flex-col gap-3">
              <Link to="/register"><Button className="w-full">Account aanmaken</Button></Link>
              <Link to="/login" className="text-sm text-gray-600 underline">Ik heb al een account</Link>
            </div>
          </>
        ) : state === 'error' ? (
          <>
            <p className="text-red-600 mb-4">{error}</p>
            <p className="text-gray-600 text-sm">Gebruik de meest recente mail van HomeMe (elke nieuwe mail bevat een verse link), of mail <a className="underline" href="mailto:info@homeme.app">info@homeme.app</a>.</p>
          </>
        ) : null}
      </Card>
    </div>
  );
};

export default ClaimPage;
